<template>
    <header>
        <nav class="navbar navbar-expand-lg navbar-light bg-light">
            <ul class="navbar-nav ml-auto align-items-center">
                <li class="nav-item mr-3">
                    <MonitoringBarCmp />
                </li>

                <li class="nav-item mr-3">
                    <ThemeToggleCmp />
                </li>

                <li class="nav-item muven-account-switcher" ref="root" @focusout="onFocusOut">
                    <button type="button" class="account-pill" ref="trigger"
                        :class="{ 'is-open': isOpen }"
                        :title="currentName"
                        aria-haspopup="dialog"
                        aria-controls="muven-account-menu"
                        :aria-expanded="isOpen ? 'true' : 'false'"
                        @click="toggle()"
                        @keydown.down.prevent="open({ focusFirstItem: true })">
                        <span class="account-avatar" aria-hidden="true">{{currentInitials}}</span>
                        <span class="account-name">{{currentName}}</span>
                        <fa icon="chevron-down" class="account-chevron" />
                    </button>

                    <transition name="account-menu">
                        <div v-if="isOpen"
                            id="muven-account-menu"
                            class="account-menu"
                            ref="menu"
                            role="dialog"
                            aria-label="Sua conta"
                            tabindex="-1"
                            @keydown="onMenuKeydown">

                            <section class="account-current account-menu-section" style="--i: 0">
                                <span class="account-avatar account-avatar-lg" aria-hidden="true">{{currentInitials}}</span>

                                <div class="account-current-text">
                                    <p class="account-eyebrow">
                                        <span class="account-eyebrow-dot"></span>
                                        Conta atual
                                    </p>
                                    <p class="account-current-name">{{currentName}}</p>
                                    <p class="account-current-document" v-if="currentDocument">{{currentDocument}}</p>
                                </div>
                            </section>

                            <section class="account-switch account-menu-section" style="--i: 1" v-if="hasMoreThanOneSubscriber">
                                <p class="account-section-title" id="muven-account-switch-title">
                                    Trocar de conta
                                    <span class="account-section-count">{{otherSubscriberList.length}}</span>
                                </p>

                                <div class="account-search" v-if="hasSearch">
                                    <fa icon="search" class="account-search-icon" />
                                    <input type="text" class="account-search-input"
                                        ref="search"
                                        v-model="subscriberFilter"
                                        data-menu-item
                                        placeholder="Buscar por nome ou documento"
                                        aria-label="Buscar conta"
                                        autocomplete="off"
                                        spellcheck="false">
                                </div>

                                <ul class="account-list" aria-labelledby="muven-account-switch-title">
                                    <li v-for="sub in filteredSubscriberList" :key="sub.publicKey || sub.name">
                                        <button type="button" class="account-option"
                                            data-menu-item
                                            :title="sub.displayName"
                                            :disabled="!!switchingKey"
                                            :class="{ 'is-switching': switchingKey === sub.key }"
                                            @click="selectSubscriber(sub.model, sub.key)">
                                            <span class="account-avatar account-avatar-sm" aria-hidden="true">{{sub.initials}}</span>

                                            <span class="account-option-text">
                                                <span class="account-option-name">{{sub.nameParts.before}}<mark v-if="sub.nameParts.match">{{sub.nameParts.match}}</mark>{{sub.nameParts.after}}</span>
                                                <span class="account-option-document" v-if="sub.document">{{sub.document}}</span>
                                            </span>

                                            <Loading v-if="switchingKey === sub.key" class="account-option-trailing" />
                                            <fa v-else icon="chevron-right" class="account-option-trailing account-option-arrow" />
                                        </button>
                                    </li>
                                </ul>

                                <p class="account-list-empty" v-if="!filteredSubscriberList.length">
                                    Nenhuma conta encontrada para "{{subscriberFilter.trim()}}"
                                </p>
                            </section>

                            <section class="account-actions account-menu-section" :style="{ '--i': hasMoreThanOneSubscriber ? 2 : 1 }">
                                <button type="button" class="account-action" data-menu-item @click="myInfo()">
                                    <span class="account-action-icon"><fa icon="user" /></span>
                                    <span class="account-action-text">
                                        <span class="account-action-label">Meus dados</span>
                                        <span class="account-action-hint" v-if="userAuthenticated.email">{{userAuthenticated.email}}</span>
                                    </span>
                                </button>

                                <button type="button" class="account-action account-action-logout" data-menu-item @click="logout()">
                                    <span class="account-action-icon"><fa icon="power-off" /></span>
                                    <span class="account-action-text">
                                        <span class="account-action-label">Sair</span>
                                    </span>
                                </button>
                            </section>
                        </div>
                    </transition>
                </li>
            </ul>
        </nav>
    </header>
</template>

<script>
import {mapState, mapGetters} from 'vuex'
import {logout} from '@/auth/services/SessionService'
import subscriberManager from '@/subscriber/managers/SubscriberManager'
import storeConstants from '@/utils/constants/StoreConstants'
import MonitoringBarCmp from '@/monitoring/components/MonitoringBarCmp'
import ThemeToggleCmp from '@/menu/components/ThemeToggleCmp'
import { toDisplayName, getInitials, formatDocument } from '@/utils/helpers/AccountNameHelper'

const SEARCH_THRESHOLD = 6

function normalize(text) {
    return (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

// separa o trecho buscado para destacar no nome, ignorando acentos e maiusculas
function splitMatch(name, term) {
    const index = term ? normalize(name).indexOf(normalize(term)) : -1

    if (index < 0) {
        return { before: name, match: '', after: '' }
    }

    return {
        before: name.slice(0, index),
        match: name.slice(index, index + term.length),
        after: name.slice(index + term.length),
    }
}

export default {
    name: 'TopMenuCmp',
    components: {
        MonitoringBarCmp,
        ThemeToggleCmp,
    },
    data() {
        return {
            isOpen: false,
            subscriberFilter: '',
            switchingKey: null,
        }
    },
    watch: {
        $route() {
            this.close()
        },
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.onDocumentMousedown)
    },
    methods: {
        async logout() {
            await logout()
        },
        myInfo() {
            this.close()
            this.$router.push('/usuarios/meus_dados')
        },
        toggle() {
            if (this.isOpen) {
                this.close()
                return
            }

            this.open()
        },
        open({ focusFirstItem = false } = {}) {
            if (this.isOpen) {
                return
            }

            this.isOpen = true
            this.subscriberFilter = ''
            document.addEventListener('mousedown', this.onDocumentMousedown)

            this.$nextTick(() => {
                // com busca, o foco vai direto para ela; sem busca, para o menu (Esc e setas funcionam)
                if (this.$refs.search) {
                    this.$refs.search.focus()
                } else if (focusFirstItem) {
                    this.focusItem(0)
                } else if (this.$refs.menu) {
                    this.$refs.menu.focus()
                }
            })
        },
        close({ returnFocus = false } = {}) {
            if (!this.isOpen) {
                return
            }

            this.isOpen = false
            document.removeEventListener('mousedown', this.onDocumentMousedown)

            if (returnFocus && this.$refs.trigger) {
                this.$refs.trigger.focus()
            }
        },
        onDocumentMousedown(event) {
            if (this.$refs.root && !this.$refs.root.contains(event.target)) {
                this.close()
            }
        },
        onFocusOut(event) {
            // fecha quando o foco sai do componente pelo teclado (Tab)
            if (event.relatedTarget && this.$refs.root && !this.$refs.root.contains(event.relatedTarget)) {
                this.close()
            }
        },
        getMenuItems() {
            return this.$refs.menu
                ? Array.from(this.$refs.menu.querySelectorAll('[data-menu-item]:not([disabled])'))
                : []
        },
        focusItem(index) {
            const items = this.getMenuItems()

            if (!items.length) {
                return
            }

            items[(index + items.length) % items.length].focus()
        },
        onMenuKeydown(event) {
            const items = this.getMenuItems()
            const current = items.indexOf(document.activeElement)

            switch (event.key) {
                case 'Escape':
                    event.preventDefault()
                    this.close({ returnFocus: true })
                    break
                case 'ArrowDown':
                    event.preventDefault()
                    this.focusItem(current + 1)
                    break
                case 'ArrowUp':
                    event.preventDefault()
                    this.focusItem(current < 0 ? items.length - 1 : current - 1)
                    break
                case 'Home':
                    if (current > 0 || current === -1) {
                        event.preventDefault()
                        this.focusItem(0)
                    }
                    break
                case 'End':
                    if (document.activeElement !== this.$refs.search) {
                        event.preventDefault()
                        this.focusItem(items.length - 1)
                    }
                    break
                case 'Enter':
                    // Enter na busca escolhe o primeiro resultado
                    if (document.activeElement === this.$refs.search && this.filteredSubscriberList.length) {
                        event.preventDefault()
                        const first = this.filteredSubscriberList[0]
                        this.selectSubscriber(first.model, first.key)
                    }
                    break
            }
        },
        selectSubscriber(sub, key) {
            const isSameSub = sub.hasSamePublicKey(subscriberManager.getPublicKeyFromStorage())

            this.$store.dispatch(storeConstants.actions.SET_SUBSCRIBER, sub)

            if (isSameSub) {
                this.close()
                return
            }

            // feedback no item enquanto a pagina recarrega com a nova conta
            this.switchingKey = key
            this.$router.push('/')
            this.$router.go()
        },
    },
    computed: {
       ...mapState({
           userAuthenticated: state => state.userAuthenticated,
           subscriberList: state => state.subscriberList,
           subscriber: state => state.subscriber,
       }),
       ...mapGetters([
           'hasMoreThanOneSubscriber',
       ]),
       currentName() {
           return toDisplayName(this.subscriber.name)
       },
       currentInitials() {
           return getInitials(this.currentName)
       },
       currentDocument() {
           return formatDocument(this.subscriber.document)
       },
       otherSubscriberList() {
           return this.subscriberList.filter(sub => !sub.hasSamePublicKey(this.subscriber.publicKey))
       },
       hasSearch() {
           return this.otherSubscriberList.length > SEARCH_THRESHOLD
       },
       filteredSubscriberList() {
           const term = this.subscriberFilter.trim()
           const termDigits = term.replace(/\D/g, '')

           return this.otherSubscriberList
               .map((sub, index) => {
                   const displayName = toDisplayName(sub.name)

                   return {
                       model: sub,
                       key: sub.publicKey || `${index}-${sub.name}`,
                       displayName,
                       initials: getInitials(displayName),
                       document: formatDocument(sub.document),
                       documentDigits: (sub.document || '').replace(/\D/g, ''),
                       nameParts: splitMatch(displayName, term),
                   }
               })
               .filter(sub => !term ||
                   !!sub.nameParts.match ||
                   (!!termDigits && sub.documentDigits.includes(termDigits)))
       },
    },
}
</script>

<style lang="scss" scoped>
$brand: var(--brand);
$brand-dark: var(--brand-strong);
$ink: var(--text-strong);
$ink-soft: var(--text-soft);
$muted: var(--text-muted);
$faint: var(--text-faint);
$line: var(--border-subtle);
$tint: var(--tint-brand-bg);
$danger: var(--danger-fg);
$shadow: var(--shadow-ink);
$ease-out: cubic-bezier(.22, 1, .36, 1);

header {
    .navbar.navbar-light.bg-light {
        height: 56px;
        background-color: var(--surface-1) !important;
    }
}

.muven-account-switcher {
    position: relative;
}

// ---------- pill ----------

.account-pill {
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: clamp(220px, 30vw, 380px);
    padding: 4px 12px 4px 4px;
    background-color: var(--tint-brand-bg);
    border: 1px solid transparent;
    border-radius: 999px;
    cursor: pointer;
    transition: background-color .15s ease, border-color .15s ease, box-shadow .2s ease;

    &:hover,
    &.is-open {
        background-color: var(--tint-brand-hover);
        border-color: var(--tint-brand-border);
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        box-shadow: 0 0 0 3px color-mix(in srgb, #{$brand} 22%, transparent);
    }

    &.is-open .account-chevron {
        transform: rotate(180deg);
        color: $brand;
    }

    .account-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: .9em;
        font-weight: 600;
        color: $ink-soft;
    }

    .account-chevron {
        flex-shrink: 0;
        font-size: .65em;
        color: $faint;
        transition: transform .25s $ease-out, color .15s ease;
    }
}

// ---------- avatar ----------

.account-avatar {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--brand-gradient);
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--white) 18%, transparent), 0 2px 6px -2px color-mix(in srgb, #{$brand} 50%, transparent);
    color: var(--text-on-brand);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .02em;
    line-height: 1;

    &.account-avatar-lg {
        width: 44px;
        height: 44px;
        font-size: 15px;
        box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--white) 18%, transparent), 0 6px 14px -6px color-mix(in srgb, #{$brand} 60%, transparent);
    }

    &.account-avatar-sm {
        width: 28px;
        height: 28px;
        font-size: 10.5px;
        background: var(--tint-brand-hover);
        box-shadow: none;
        color: $brand;
        transition: background .15s ease, color .15s ease;
    }
}

// ---------- menu ----------

.account-menu {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    z-index: 100000;
    width: 340px;
    max-width: calc(100vw - 32px);
    overflow: hidden;
    background: var(--surface-overlay);
    border: 1px solid $line;
    border-radius: 14px;
    box-shadow:
        0 1px 2px color-mix(in srgb, #{$shadow} 6%, transparent),
        0 12px 32px -8px color-mix(in srgb, #{$shadow} 16%, transparent),
        0 28px 60px -28px color-mix(in srgb, #{$brand} 28%, transparent);
    transform-origin: top right;

    &:focus {
        outline: none;
    }

    p {
        margin-bottom: 0;
    }
}

.account-menu-enter-active {
    transition: opacity .18s ease, transform .26s $ease-out;

    .account-menu-section {
        animation: account-section-rise .32s $ease-out both;
        animation-delay: calc(40ms + var(--i, 0) * 35ms);
    }
}

.account-menu-leave-active {
    transition: opacity .12s ease-in, transform .12s ease-in;
}

.account-menu-enter,
.account-menu-leave-to {
    opacity: 0;
    transform: translateY(-6px) scale(.97);
}

@keyframes account-section-rise {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: none; }
}

.account-menu-section + .account-menu-section {
    border-top: 1px solid $line;
}

// conta atual: nome completo, sem corte
.account-current {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 18px 18px 16px;
    background:
        radial-gradient(120% 140% at 0% 0%, color-mix(in srgb, var(--brand-soft) 13%, transparent), transparent 60%),
        var(--tint-brand-soft);

    .account-current-text {
        min-width: 0;
    }

    .account-eyebrow {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 3px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .08em;
        text-transform: uppercase;
        color: $brand;
    }

    .account-eyebrow-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--success-solid);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--success-solid) 18%, transparent);
    }

    .account-current-name {
        font-size: 15px;
        font-weight: 700;
        line-height: 1.3;
        color: $ink;
        overflow-wrap: anywhere;
    }

    .account-current-document {
        margin-top: 4px;
        font-size: 12px;
        font-variant-numeric: tabular-nums;
        letter-spacing: .02em;
        color: $muted;
    }
}

// trocar de conta
.account-switch {
    padding: 12px 6px 6px;
}

.account-section-title {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 12px 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: $muted;

    .account-section-count {
        padding: 1px 6px;
        border-radius: 999px;
        background: var(--tint-brand-bg);
        font-size: 10.5px;
        letter-spacing: 0;
        color: $ink-soft;
    }
}

.account-search {
    position: relative;
    margin: 0 6px 6px;

    .account-search-icon {
        position: absolute;
        top: 50%;
        left: 12px;
        transform: translateY(-50%);
        font-size: 12px;
        color: $faint;
        pointer-events: none;
    }

    .account-search-input {
        width: 100%;
        height: 36px;
        padding: 0 12px 0 32px;
        font-family: inherit;
        font-size: 13.5px;
        color: $ink;
        background: var(--tint-brand-bg);
        border: 1px solid transparent;
        border-radius: 10px;
        transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;

        &::placeholder {
            color: $faint;
        }

        &:focus {
            outline: none;
            background: var(--surface-1);
            border-color: color-mix(in srgb, #{$brand} 45%, transparent);
            box-shadow: 0 0 0 3px color-mix(in srgb, #{$brand} 12%, transparent);
        }
    }
}

.account-list {
    max-height: 264px;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    list-style: none;
}

.account-option {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 10px 7px 8px;
    text-align: left;
    background: none;
    border: 0;
    border-radius: 10px;
    cursor: pointer;
    transition: background-color .12s ease;

    .account-option-text {
        display: flex;
        flex: 1;
        flex-direction: column;
        min-width: 0;
    }

    .account-option-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 13.5px;
        font-weight: 600;
        color: $ink-soft;

        mark {
            padding: 0;
            border-radius: 3px;
            background: color-mix(in srgb, #{$brand} 14%, transparent);
            color: $brand-dark;
        }
    }

    .account-option-document {
        font-size: 11.5px;
        font-variant-numeric: tabular-nums;
        color: $muted;
    }

    .account-option-trailing {
        flex-shrink: 0;
        margin: 0;
        font-size: 11px;
        color: $brand;
    }

    .account-option-arrow {
        opacity: 0;
        transform: translateX(-4px);
        transition: opacity .15s ease, transform .2s $ease-out;
    }

    &:hover,
    &:focus-visible {
        background-color: $tint;

        .account-avatar-sm {
            background: var(--brand-gradient);
            color: var(--text-on-brand);
        }

        .account-option-name {
            color: $ink;
        }

        .account-option-arrow {
            opacity: 1;
            transform: none;
        }
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        box-shadow: inset 0 0 0 2px color-mix(in srgb, #{$brand} 35%, transparent);
    }

    &:disabled {
        cursor: progress;

        &:not(.is-switching) {
            opacity: .5;
        }
    }
}

.account-list-empty {
    padding: 14px 12px 12px;
    font-size: 13px;
    text-align: center;
    color: $muted;
}

// acoes
.account-actions {
    padding: 6px;
}

.account-action {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 10px 7px 8px;
    text-align: left;
    background: none;
    border: 0;
    border-radius: 10px;
    cursor: pointer;
    transition: background-color .12s ease;

    .account-action-icon {
        display: inline-flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        background: var(--tint-brand-bg);
        font-size: 12px;
        color: $ink-soft;
        transition: background-color .15s ease, color .15s ease;
    }

    .account-action-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .account-action-label {
        font-size: 13.5px;
        font-weight: 600;
        color: $ink-soft;
    }

    .account-action-hint {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 11.5px;
        color: $muted;
    }

    &:hover,
    &:focus-visible {
        background-color: $tint;

        .account-action-icon {
            background: var(--surface-1);
            color: $brand;
            box-shadow: 0 1px 2px color-mix(in srgb, #{$shadow} 8%, transparent);
        }
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        box-shadow: inset 0 0 0 2px color-mix(in srgb, #{$brand} 35%, transparent);
    }

    &.account-action-logout {
        .account-action-label {
            color: $danger;
        }

        .account-action-icon {
            color: $danger;
        }

        &:hover,
        &:focus-visible {
            background-color: var(--danger-bg);

            .account-action-icon {
                background: var(--danger-solid);
                color: var(--text-on-brand);
            }
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .account-pill .account-chevron,
    .account-option .account-option-arrow {
        transition: none;
    }

    .account-menu-enter-active,
    .account-menu-leave-active {
        transition: opacity .12s ease;

        .account-menu-section {
            animation: none;
        }
    }

    .account-menu-enter,
    .account-menu-leave-to {
        transform: none;
    }
}
</style>
